import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function ForcePasswordChange() {
  const { user, refresh, logout } = useAuth();
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPwd.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (newPwd !== confirmPwd) { toast.error("New passwords do not match"); return; }
    if (currentPwd === newPwd) { toast.error("New password must be different from the current password"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPwd,
        new_password: newPwd,
      });
      toast.success("Password updated. Welcome!");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 bg-secondary/40"
      data-testid="force-password-change-page"
    >
      <Card className="w-full max-w-md p-8 border-border bg-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-primary" />
          </div>
          <div>
            <div className="eyebrow text-muted-foreground">First-time login</div>
            <h1 className="font-display text-2xl font-bold">Set a new password</h1>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Hi <span className="font-medium">{user?.name}</span>, for your security you must
          replace the temporary password before continuing.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Temporary Password *</Label>
            <Input
              required
              type="password"
              autoComplete="current-password"
              value={currentPwd}
              onChange={(e) => setCurrentPwd(e.target.value)}
              data-testid="fpc-current-password"
            />
          </div>
          <div className="space-y-2">
            <Label>New Password *</Label>
            <Input
              required
              type="password"
              autoComplete="new-password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              data-testid="fpc-new-password"
              placeholder="Min 6 characters"
            />
          </div>
          <div className="space-y-2">
            <Label>Confirm New Password *</Label>
            <Input
              required
              type="password"
              autoComplete="new-password"
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              data-testid="fpc-confirm-password"
            />
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={saving}
            data-testid="fpc-submit"
          >
            <KeyRound className="w-4 h-4 mr-2" />
            {saving ? "Updating..." : "Update Password & Continue"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={logout}
            data-testid="fpc-logout"
          >
            Sign out
          </Button>
        </form>
      </Card>
    </div>
  );
}
